/**
 * @file auth-slice.ts
 * @description Redux slice для управления состоянием аутентификации пользователей в Task Manager приложении. Обеспечивает централизованное управление процессами входа и выхода, хранение JWT токенов (access/refresh), состояний загрузки и обработку ошибок аутентификации с автоматическими редиректами и интеграцией с localStorage для персистентного хранения токенов между сессиями.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-22
 * @dependencies ['@reduxjs/toolkit', './auth-actions', '@utils/token-helper']
 * @related-files ['src/features/auth/auth-actions.ts', 'src/utils/token-helper.ts', 'src/routes/private-route.tsx', 'src/pages/login/login.component.tsx', 'src/store/index.ts']
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { userLogin, userLogout } from './auth-actions';
import { getToken } from '@utils/token-helper';

interface IInitialState {
    loading: boolean;
    username: string;
    accessToken: string | null;
    refreshToken: string | null;
    error: string | null;
    success: boolean;
}

const initialState: IInitialState = {
    loading: false,
    username: '',
    accessToken: getToken(), // for storing the JWT
    refreshToken: localStorage.getItem(`${import.meta.env.VITE_BASE_URL}_refreshToken`), // for storing the JWT
    error: null,
    success: false // for monitoring the registration process.
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(userLogin.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(userLogin.fulfilled, (state, { payload }: PayloadAction<IInitialState>) => {
            state.loading = false;
            state.username = payload.username;
            state.accessToken = payload.accessToken;
            state.refreshToken = payload.refreshToken;
            window.location.href = import.meta.env.VITE_BASE_URL;
        });
        builder.addCase(userLogin.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });
        builder.addCase(userLogout.fulfilled, () => {
            localStorage.removeItem(`${import.meta.env.VITE_BASE_URL}_accessToken`);
            localStorage.removeItem(`${import.meta.env.VITE_BASE_URL}_refreshToken`);
            window.location.href = `${import.meta.env.VITE_BASE_URL}login`;
        });
    }
});

export default authSlice.reducer;
